import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "../../../components/button";
import { CustomerLoader } from "../../../components/customer-loader";
import { useGetBulkPurchaseTransactionByReferenceForCustomerQuery } from "../../../../../service/transaction-service";
import { useLazyDownloadBulkPurchaseInvoiceQuery } from "../../../../../service/bulk-purchase-service";
import { formatCurrency } from "../../../../../utils";
import { getErrorMessage } from "../../../../../utils/error-handler";
import styles from "./bulk-success.module.scss";

interface BulkSuccessProps {
	reference: string;
	currency: string;
	onDone: () => void;
}

const CheckIcon: React.FC = () => (
	<svg width="56" height="56" viewBox="0 0 56 56" fill="none" aria-hidden="true">
		<circle cx="28" cy="28" r="28" fill="#12B76A" fillOpacity="0.12" />
		<circle cx="28" cy="28" r="18" fill="#12B76A" />
		<path d="M21 28.5l4.5 4.5L35 23" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

export const BulkSuccess: React.FC<BulkSuccessProps> = ({ reference, currency, onDone }) => {
	const { t } = useTranslation();
	const { data: transaction, isLoading } = useGetBulkPurchaseTransactionByReferenceForCustomerQuery(reference, {
		skip: !reference,
	});
	const [downloadInvoice, { isFetching: isDownloading }] = useLazyDownloadBulkPurchaseInvoiceQuery();

	const bulk = transaction?.bulkPurchaseDetails;
	const vendingItems = bulk?.vendingDetailsList ?? [];
	const billingItems = bulk?.billingDetailsList ?? [];
	const totalMeters = vendingItems.length + billingItems.length;

	const handleDownload = async () => {
		try {
			const blob = await downloadInvoice(reference).unwrap();
			const url = window.URL.createObjectURL(blob);
			window.open(url, "_blank", "noopener,noreferrer");
			window.URL.revokeObjectURL(url);
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) ?? t("buyElectricity.bulk.failedToDownloadReceipt"));
		}
	};

	return (
		<div className={styles.container}>
			<div className={styles.header}>
				<CheckIcon />
				<h1 className={styles.title}>{t("payment.paymentSuccessfull")}</h1>
				<p className={styles.subtitle}>{t("buyElectricity.bulk.metersPurchased", { count: totalMeters })}</p>
			</div>

			{isLoading ? (
				<div className={styles.loaderWrap}>
					<CustomerLoader />
				</div>
			) : (
				<>
					<div className={styles.summaryCard}>
						<div className={styles.summaryRow}>
							<span>{t("buyElectricity.totalAmount")}</span>
							<span className={styles.strong}>
								{transaction?.amount
									? formatCurrency(transaction.amount.amount, { currency: transaction.amount.currency })
									: formatCurrency(0, { currency })}
							</span>
						</div>
						<div className={styles.summaryRow}>
							<span>{t("buyElectricity.bulk.totalMeters")}</span>
							<span className={styles.strong}>{totalMeters}</span>
						</div>
					</div>

					<div className={styles.list}>
						{vendingItems.map((item, i) => (
							<div key={`v-${i}`} className={styles.item}>
								<div className={styles.itemInfo}>
									<span className={styles.itemNumber}>{item.meterNumber || item.accountNumber}</span>
									<span className={styles.itemType}>{t("common.labels.prepaid")}</span>
									{item.vendingToken?.token && (
										<span className={styles.token}>
											{t("buyElectricity.hereIsYourToken")}: <strong>{item.vendingToken.token}</strong>
										</span>
									)}
								</div>
								<span className={styles.itemAmount}>
									{item.amount ? formatCurrency(item.amount.amount, { currency: item.amount.currency }) : ""}
								</span>
							</div>
						))}
						{billingItems.map((item, i) => (
							<div key={`b-${i}`} className={styles.item}>
								<div className={styles.itemInfo}>
									<span className={styles.itemNumber}>{item.accountNumber || item.meterNumber}</span>
									<span className={styles.itemType}>{t("common.labels.postpaid")}</span>
								</div>
								<span className={styles.itemAmount}>
									{item.amount ? formatCurrency(item.amount.amount, { currency: item.amount.currency }) : ""}
								</span>
							</div>
						))}
					</div>
				</>
			)}

			<div className={styles.actions}>
				<Button variant="outline" fullWidth disabled={isDownloading || !transaction} onClick={handleDownload}>
					{isDownloading ? t("buyElectricity.bulk.downloading") : t("buyElectricity.bulk.downloadReceipt")}
				</Button>
				<Button variant="primary" fullWidth className={styles.primaryAction} onClick={onDone}>
					{t("common.pages.backToHome")}
				</Button>
			</div>
		</div>
	);
};

export default BulkSuccess;
